// Materialverbrauch am Ticket — was der Hausmeister vor Ort erfasst hat, plus
// Einkaufspreise für die Weiterberechnung an Eigentümer oder WEG.
// Läuft als Tab im „Einsatz“-Widget: Titel und Karte liefert das Widget.
import React, { useCallback, useEffect, useState } from 'react';
import { Package, SpinnerGap, Trash, WarningCircle, X } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import { ladeMaterial, materialErfassen, materialLoeschen, type Material } from '../../lib/hausmeister/api';
import { AppKachel } from '../ui/AppIconKnopf';

interface Props {
  ticketId: string;
  userProfile?: { id?: string } | null;
  /** Anzahl Material-Positionen (Zähler im Einsatz-Tab) */
  onAnzahl?: (n: number) => void;
}

const FELD: React.CSSProperties = {
  height: 38, borderRadius: 11, padding: '0 10px', border: 'none', outline: 'none', background: 'var(--bg)',
  boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13.5, minWidth: 0, width: '100%',
};
const LABEL: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--ink3)' };
const BADGE: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 11, fontSize: 11, fontWeight: 800,
};
const euro = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const TicketMaterial: React.FC<Props> = ({ ticketId, userProfile, onAnzahl }) => {
  const meineId = userProfile?.id || '';
  const [material, setMaterial] = useState<Material[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [neu, setNeu] = useState<{ bezeichnung: string; menge: string; einheit: string; preis: string } | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setMaterial(await ladeMaterial(ticketId));
    setLaedt(false);
  }, [ticketId]);

  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => { onAnzahl?.(material.length); }, [material.length, onAnzahl]);

  // Preise können nachgetragen werden — der Hausmeister erfasst vor Ort meist nur die Menge.
  const preisSetzen = async (m: Material, wert: string) => {
    const preis = Number(wert.replace(',', '.'));
    if (!Number.isFinite(preis) || preis < 0) return;
    const { error } = await supabase.from('ticket_material').update({ einzelpreis: preis }).eq('id', m.id);
    if (error) { setFehler(error.message); return; }
    void laden();
  };

  const hinzufuegen = async () => {
    if (!neu) return;
    if (!neu.bezeichnung.trim()) { setFehler('Bitte eine Bezeichnung angeben.'); return; }
    setArbeitet(true);
    const preis = neu.preis.trim() ? Number(neu.preis.replace(',', '.')) : null;
    const problem = await materialErfassen({
      ticketId, meineId, bezeichnung: neu.bezeichnung,
      menge: Number(neu.menge.replace(',', '.')) || 1, einheit: neu.einheit || 'Stk',
      einzelpreis: preis !== null && Number.isFinite(preis) ? preis : null,
    });
    setArbeitet(false);
    if (problem) { setFehler(problem); return; }
    setNeu(null);
    setFehler(null);
    void laden();
  };

  const loeschen = async (m: Material) => {
    if (!window.confirm(`„${m.bezeichnung}" entfernen?`)) return;
    await materialLoeschen(m.id);
    void laden();
  };

  const summe = material.reduce((s, m) => s + Number(m.menge) * Number(m.einzelpreis || 0), 0);
  const ohnePreis = material.filter((m) => m.einzelpreis === null).length;

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {(summe > 0 || ohnePreis > 0) && (
        <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
          {summe > 0 && (
            <span className="tabular-nums" data-tip="Summe – Material mit Preis, netto" style={{ ...BADGE, background: 'var(--fill2)', color: 'var(--ink2)' }}>{euro(summe)}</span>
          )}
          {ohnePreis > 0 && (
            <span data-tip="Ohne Preis – Positionen, bei denen der Einzelpreis noch fehlt" style={{ ...BADGE, background: 'var(--warn-soft)', color: 'var(--warn)' }}>{ohnePreis} ohne Preis</span>
          )}
        </div>
      )}

      {fehler && (
        <div role="alert" className="flex items-start" style={{ gap: 8, padding: '9px 12px', borderRadius: 12, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 12.5, fontWeight: 700 }}>
          <WarningCircle size={16} weight="fill" className="shrink-0" />
          <span className="flex-1">{fehler}</span>
          <button type="button" onClick={() => setFehler(null)} aria-label="Hinweis schließen" style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}>
            <X size={13} weight="bold" />
          </button>
        </div>
      )}

      {neu && (
        <div className="flex flex-col" style={{ gap: 8, padding: 12, borderRadius: 16, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1.5px var(--acc)' }}>
          <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Bezeichnung</span>
            <input value={neu.bezeichnung} onChange={(e) => setNeu({ ...neu, bezeichnung: e.target.value })} placeholder="Bezeichnung"
              style={FELD} data-tip="Bezeichnung – Welches Material?" /></label>
          <div className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Menge</span>
              <input value={neu.menge} onChange={(e) => setNeu({ ...neu, menge: e.target.value })} inputMode="decimal" style={FELD} data-tip="Menge" /></label>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Einheit</span>
              <input value={neu.einheit} onChange={(e) => setNeu({ ...neu, einheit: e.target.value })} style={FELD} data-tip="Einheit – z. B. Stk, m, kg" /></label>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Preis</span>
              <input value={neu.preis} onChange={(e) => setNeu({ ...neu, preis: e.target.value })} placeholder="€/Stk" inputMode="decimal" style={FELD} data-tip="Preis – Je Einheit in €" /></label>
          </div>
          <div className="flex items-center justify-end" style={{ gap: 6 }}>
            <button type="button" onClick={() => setNeu(null)} style={{ height: 34, padding: '0 12px', borderRadius: 17, border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700 }}>
              Abbrechen
            </button>
            <button type="button" onClick={() => void hinzufuegen()} disabled={arbeitet} data-tip="Position hinzufügen – Material auf das Ticket buchen"
              className="inline-flex items-center justify-center"
              style={{ height: 34, padding: '0 14px', borderRadius: 17, border: 'none', cursor: 'pointer', background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, opacity: arbeitet ? 0.6 : 1 }}>
              {arbeitet ? <SpinnerGap size={14} className="animate-spin" /> : 'Hinzufügen'}
            </button>
          </div>
        </div>
      )}

      {laedt ? (
        <div className="flex justify-center" style={{ padding: '16px 0', color: 'var(--ink3)' }}><SpinnerGap size={16} className="animate-spin" /></div>
      ) : material.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Kein Material erfasst.</p>
      ) : (
        <div className="flex flex-col">
          {material.map((m) => (
            <div key={m.id} className="flex items-center" style={{ gap: 10, padding: '8px 2px', borderBottom: '1px solid var(--line)', fontSize: 12.5 }}>
              <span className="shrink-0 tabular-nums" style={{ width: 64, fontWeight: 600, color: 'var(--ink3)' }}>
                {Number(m.menge).toLocaleString('de-DE')} {m.einheit}
              </span>
              <span className="min-w-0 flex-1 truncate" style={{ fontWeight: 800 }}>{m.bezeichnung}</span>
              <input
                defaultValue={m.einzelpreis !== null ? String(m.einzelpreis).replace('.', ',') : ''}
                onBlur={(e) => void preisSetzen(m, e.target.value)}
                placeholder="€/Stk"
                inputMode="decimal"
                aria-label={`Preis je Einheit für ${m.bezeichnung}`}
                data-tip="Preis – Je Einheit in €"
                className="shrink-0 text-right tabular-nums"
                style={{ ...FELD, width: 72, height: 30, borderRadius: 9, padding: '0 8px', fontSize: 12.5 }}
              />
              <span className="shrink-0 text-right tabular-nums" style={{ width: 72, fontWeight: 800 }}>
                {m.einzelpreis !== null ? euro(Number(m.menge) * Number(m.einzelpreis)) : '—'}
              </span>
              <button type="button" aria-label="Löschen" data-tip="Löschen" onClick={() => void loeschen(m)}
                className="inline-flex shrink-0 items-center justify-center" style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--bad-soft)', color: 'var(--bad)' }}>
                <Trash size={13} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!neu && (
        <AppKachel icon={Package} farbe="orange" titel="Position hinzufügen" unter="Material auf das Ticket buchen"
          tip="Position hinzufügen – Material auf das Ticket buchen"
          onClick={() => setNeu({ bezeichnung: '', menge: '1', einheit: 'Stk', preis: '' })} />
      )}
    </div>
  );
};

export default TicketMaterial;
